import { EyeOpenIcon } from '@radix-ui/react-icons';
import { Tooltip } from 'radix-ui';
import React from 'react';

import { useTranslation } from '~/app';
import { ChatDisplayItem } from '~/app/selectors';
import { cn } from '~/utils/styles';

import ChatAvatar from './ChatAvatar';

export interface ChatMessageRowProps {
  /** Message display item (must have type 'message' and model). */
  item: ChatDisplayItem & { type: 'message'; model: NonNullable<ChatDisplayItem['model']> };
  /** Resolved sender display name (use fallback in parent when empty). */
  senderDisplayName: string;
  /** Grouping: message begins a sender group. */
  startsGroup?: boolean;
  /** Grouping: message ends a sender group. */
  endsGroup?: boolean;
  /** When true (first row of a group), sender header + wide avatar are rendered in the list sticky bar. */
  suppressGroupHeader?: boolean;
  /** When true, wide layout omits the avatar column (parent group provides it). */
  wideBubbleColumnOnly?: boolean;
  onAvatarClick?: (userId: string) => void;
}

const ChatMessageRow: React.FC<ChatMessageRowProps> = ({
  item,
  senderDisplayName,
  startsGroup = true,
  endsGroup = true,
  suppressGroupHeader = false,
  wideBubbleColumnOnly = false,
}) => {
  const { t } = useTranslation();
  const isOutgoing = item.model.direction === 'outgoing';

  const toneClasses = cn(
    isOutgoing && 'bg-[#9ca965]',
    !isOutgoing && item.isSpectator && 'bg-[#e2e8f0] border border-[#cbd5e1]',
    !isOutgoing && !item.isSpectator && 'bg-[#C5D386]',
  );

  const bubbleClasses = cn(
    'relative inline-block w-fit max-w-full rounded-lg px-2.5 py-1.5 text-sm shadow-sm shadow-black/15',
    '@[18rem]:max-w-full @[18rem]:min-h-9 @[18rem]:px-3 @[18rem]:py-2',
    !startsGroup && (isOutgoing ? 'rounded-tr-sm' : 'rounded-tl-sm'),
    !endsGroup && (isOutgoing ? 'rounded-br-sm' : 'rounded-bl-sm'),
    toneClasses,
  );

  const tailClasses = cn(
    'pointer-events-none absolute bottom-1 z-0 h-3 w-3 rotate-45 rounded-[2px]',
    isOutgoing ? 'right-1' : 'left-1',
    toneClasses,
    !isOutgoing && item.isSpectator && 'shadow-none',
  );

  const showHeader = startsGroup && !suppressGroupHeader;

  const senderRow = (
    <span className={cn('inline-flex min-w-0 items-center gap-1 @[18rem]:gap-2')}>
      <ChatAvatar
        userId={item.userId}
        username={senderDisplayName}
        size="sm"
        className="inline-flex h-2.5! w-2.5! shrink-0 text-[7px]! leading-none! @[18rem]:hidden"
      />
      <span className="inline-flex min-w-0 items-center gap-0.5">
        <span className="min-w-0 truncate text-xs font-bold text-gray-700 @[18rem]:text-sm">
          {senderDisplayName}
        </span>
        {item.isSpectator ? (
          <Tooltip.Root>
            <Tooltip.Trigger asChild>
              <button
                type="button"
                className={cn(
                  'inline-flex shrink-0 cursor-default items-center justify-center rounded border-0 bg-transparent p-0.5 align-middle',
                  '-m-0.5 text-slate-400/80 @[18rem]:text-slate-400/70',
                  'hover:text-slate-500 focus-visible:ring-2 focus-visible:ring-slate-300 focus-visible:outline-none',
                )}
                aria-label={t('chat_spectator_badge')}
              >
                <EyeOpenIcon
                  className="pointer-events-none h-2.5 w-2.5 @[18rem]:h-3 @[18rem]:w-3"
                  aria-hidden
                />
              </button>
            </Tooltip.Trigger>
            <Tooltip.Portal>
              <Tooltip.Content
                side="top"
                sideOffset={6}
                className="z-400 rounded-lg border border-gray-100 bg-white px-2 py-1 text-xs text-slate-700 shadow-xl"
              >
                {t('chat_spectator_badge')}
                <Tooltip.Arrow className="fill-white" />
              </Tooltip.Content>
            </Tooltip.Portal>
          </Tooltip.Root>
        ) : null}
      </span>
    </span>
  );

  return (
    <div
      className={cn(
        'w-full',
        startsGroup && !suppressGroupHeader
          ? 'pt-1'
          : startsGroup && suppressGroupHeader
            ? 'pt-0'
            : 'pt-[2px]',
        endsGroup ? 'pb-1' : 'pb-[2px]',
      )}
      {...(item.isSpectator ? { 'data-chat-spectator': '' } : {})}
      data-chat-direction={item.model.direction}
      data-chat-incoming={item.model.direction === 'incoming' ? 'true' : undefined}
      data-chat-event-log-index={item.eventLogIndex ?? undefined}
    >
      {showHeader && (
        <div
          className={cn(
            'mb-0.5 hidden items-center gap-1.5 text-[10px] text-gray-500 @[18rem]:flex @[18rem]:w-full @[18rem]:gap-2',
            isOutgoing ? 'justify-end text-right' : 'justify-start text-left',
          )}
        >
          <span className={cn('min-w-0')}>{senderRow}</span>
          <span className="shrink-0 opacity-100">{item.model.sentTime}</span>
        </div>
      )}

      <div
        className={cn(
          'flex w-full flex-col',
          !wideBubbleColumnOnly && '@[18rem]:flex-row @[18rem]:items-start @[18rem]:gap-2',
          wideBubbleColumnOnly && isOutgoing && '@[18rem]:flex-col @[18rem]:items-end',
          wideBubbleColumnOnly && !isOutgoing && '@[18rem]:flex-col @[18rem]:items-start',
          !wideBubbleColumnOnly && (isOutgoing ? '@[18rem]:flex-row-reverse' : '@[18rem]:flex-row'),
        )}
      >
        {!wideBubbleColumnOnly ? (
          <div
            className="hidden w-9 shrink-0 self-start @[18rem]:block"
            aria-hidden={!startsGroup || !!suppressGroupHeader}
          >
            {startsGroup && !suppressGroupHeader ? (
              <ChatAvatar
                userId={item.userId}
                username={senderDisplayName}
                size="lg"
                className="h-9 w-9"
              />
            ) : null}
          </div>
        ) : null}

        <div
          className={cn('flex min-w-0 flex-1 flex-col', isOutgoing ? 'items-end' : 'items-start')}
        >
          {showHeader && (
            <div
              className={cn(
                'mb-0.5 flex items-center gap-1.5 text-[10px] text-gray-500 @[18rem]:hidden',
              )}
            >
              <span className={cn('min-w-0', isOutgoing && 'text-right')}>{senderRow}</span>
              <span className="shrink-0 opacity-80">{item.model.sentTime}</span>
            </div>
          )}
          <div className={bubbleClasses}>
            {endsGroup && <span aria-hidden className={tailClasses} />}
            <span className="relative z-10 break-words whitespace-pre-wrap">
              {item.model.message || ''}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatMessageRow;
